import { useMemo, useState } from 'react'
import { Flex } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'

import { BudgetFormEdit } from '@/components/BudgetFormEdit/BudgetFormEdit'
import { BudgetFormModal } from '@/components/BudgetFormModal/BudgetFormModal'
import { useBudgetItemDelete } from '@/components/BudgetsList/BudgetsList.config'
import type { BudgetsListProps } from '@/components/BudgetsList/BudgetsList.types'
import { BudgetsListItem } from '@/components/BudgetsListItem/BudgetsListitem'
import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'

export function BudgetsList({
  budgets,
  currentBudget,
  readOnly,
}: BudgetsListProps) {
  const [openedDeleteModal, setOpenedDeleteModal] = useState<boolean>(false)
  const [openedEditModal, setOpenedEditModal] = useState<boolean>(false)
  const [currentBudgetId, setCurrentBudgetId] = useState<string>('')

  const isMutating = useIsMutating({
    exact: true,
  })

  const { deleteBudget } = useBudgetItemDelete(currentBudgetId)

  const handleOpenDeleteModal = (id: string | undefined) => {
    if (!id) return

    setCurrentBudgetId(id)
    setOpenedDeleteModal(true)
  }

  const handleOpenEditModal = (id: string | undefined) => {
    if (!id) return

    setCurrentBudgetId(id)
    setOpenedEditModal(true)
  }

  const currentOpenedBudget = useMemo(() => {
    if (!currentBudgetId || !budgets) return undefined

    const findCurrentBudget = budgets.find(
      budget => budget.id === parseInt(currentBudgetId, 10),
    )

    if (!findCurrentBudget) return undefined

    return findCurrentBudget
  }, [budgets, currentBudgetId])

  if (!budgets?.length) {
    return null
  }

  return (
    <>
      <ScrollArea
        h={{
          base: budgets.length > 3 ? 300 : 'auto',
          sm: budgets.length > 5 ? 450 : 'auto',
        }}
        mt={32}
        scrollbars="y"
      >
        <Flex direction="column" gap={8}>
          {budgets.map(budget => (
            <BudgetsListItem
              key={budget.id}
              budget={budget}
              openDeleteModal={handleOpenDeleteModal}
              openEditModal={handleOpenEditModal}
              readOnly={readOnly}
            />
          ))}
        </Flex>
      </ScrollArea>

      {!readOnly && (
        <>
          <ConfirmationModal
            button={{ label: 'Delete', variant: 'error' }}
            description="Are you sure you want to delete budget?"
            isLoading={!!isMutating}
            onClose={() => setOpenedDeleteModal(false)}
            onConfirm={deleteBudget}
            opened={openedDeleteModal}
            title="Delete account"
          />

          <BudgetFormModal
            onClose={() => setOpenedEditModal(false)}
            opened={openedEditModal}
          >
            <BudgetFormEdit
              budget={currentOpenedBudget}
              budgetId={currentBudgetId}
              currentBudget={currentBudget}
              onClose={() => setOpenedEditModal(false)}
            />
          </BudgetFormModal>
        </>
      )}
    </>
  )
}
